import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

import {
  PickersDay,
  PickersCalendarHeaderProps,
  PickersDayProps,
} from "@mui/x-date-pickers";
import { Avatar, Box } from "@mui/material";

import { ContainedButton, OutlinedButton } from "../Button/AppButton";

import CircleIcon from "@mui/icons-material/Circle";
import { formattedDate } from "./common";
import { DamsAppAsset } from "../../assets/registry";
import { grey } from "@mui/material/colors";
const CustomCalendarHeaderRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "8px 16px",
  alignItems: "center",
});

interface DatePickerProps {
  value?: Dayjs;
  handleOnchange?: (date: string) => void;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  width?: number | string;
  placeholder?: string;
  isBasic?: boolean;
  required?: boolean;
}

export function DatePicker({
  handleOnchange,
  value,
  label,
  error,
  errorMessage,
  width = "100%",
  placeholder = "Select date",
  isBasic = false,
  required = false,
}: Readonly<DatePickerProps>) {
  const initialValue: Dayjs = value ?? dayjs();
  const [datePicked, updateDatePicked] = React.useState<Dayjs | null>(
    initialValue
  );
  const updateDatehelper = (date: Dayjs) => updateDatePicked(date);

  const [showModel, setModelState] = React.useState<boolean>(false);
  const [dateText, setDateText] = React.useState(
    formattedDate(datePicked ?? dayjs(), placeholder)
  );
  const updateModelState = () => setModelState(true);

  const isInvalidDate = datePicked == null;

  const updatePropsState = () => {
    if (isInvalidDate) {
      return;
    }

    if (handleOnchange)
      handleOnchange((datePicked ?? dayjs()).format("YYYY-MM-DD"));
    setDateText(`${formattedDate(datePicked)}`);
    setModelState(false);
  };

  const styles = getStyles(isBasic);
  return (
    <Box sx={styles.container}>
      <Stack
        spacing={isBasic ? -0.5 : 0}
        height={isBasic ? undefined : 75}
        width={"100%"}
      >
        <Typography
          fontFamily={"Inter"}
          fontWeight={isBasic ? 400 : 500}
          fontSize={isBasic ? 12 : 14}
          color={isBasic ? "#667085" : "#344054"}
          minHeight={22}
        >
          {label}
          {required && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </Typography>
        <IconButton sx={{ borderRadius: 1, p: 0 }} onClick={updateModelState}>
          <Box sx={[styles.labelContainer, { width: width }]}>
            <Avatar
              src={
                DamsAppAsset.icons[
                  isInvalidDate ? "calender_icon" : "calender_icon_fill"
                ]
              }
              sx={{ width: 20, height: 20 }}
            />
            <CalenderLabel
              title={dateText}
              fontWeight="500"
              color={grey[600]}
            />
          </Box>
        </IconButton>
        {error && (
          <Typography variant="caption" color={"red"}>
            {errorMessage}
          </Typography>
        )}
      </Stack>

      <DatePickerModel open={showModel}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction={"row"}>
            <Box>
              <DateCalendar
                value={datePicked}
                sx={styles.dateComponent}
                showDaysOutsideCurrentMonth
                fixedWeekNumber={6}
                slots={{
                  calendarHeader: (props) =>
                    CustomCalendarHeader({ props, datePicked }),
                  day: (props) =>
                    CustomDayDate({
                      props,
                      updateDatehelper,
                      datePicked,
                    }),
                }}
              />

              <Stack
                sx={{ height: 72 }}
                direction={"row"}
                alignItems={"center"}
                paddingX={2}
              >
                <Stack direction={"row"} gap={1.5}>
                  <OutlinedButton
                    width={142}
                    title="cancel"
                    onClick={() => setModelState(false)}
                  />
                  <ContainedButton
                    width={142}
                    title="confirm"
                    onClick={updatePropsState}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </LocalizationProvider>
      </DatePickerModel>
    </Box>
  );
}

const DatePickerModel = ({
  children,
  open,
}: {
  open: boolean;
  children: React.ReactElement;
}) => {
  if (!open) return null;
  const styles = getStyles(false);
  return (
    <Box width={328} sx={styles.model}>
      {children}
    </Box>
  );
};

const CustomDayDate = ({
  props,
  updateDatehelper,
  datePicked,
}: {
  props: PickersDayProps<Dayjs>;
  updateDatehelper: (date: Dayjs) => void;
  datePicked: Dayjs | null;
}) => {
  const { outsideCurrentMonth, day, ...other } = props;

  const onClick = React.useCallback(() => {
    if (outsideCurrentMonth) {
      return;
    }
    updateDatehelper(day);
  }, [day, outsideCurrentMonth, updateDatehelper]);

  const IndicatorStyle = React.useMemo(
    () =>
      datePicked?.isSame(day, "day") ? { color: "white" } : { display: "none" },
    [datePicked, day]
  );
  const styles = getStyles(false);
  return (
    <Box sx={styles.dayPickerContainer}>
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        onClick={onClick}
        sx={styles.dayContainer}
      />
      <CircleIcon sx={[styles.pickedDayIndicator, IndicatorStyle]} />
    </Box>
  );
};

const CustomCalendarHeader = ({
  props,
  datePicked,
}: {
  props: PickersCalendarHeaderProps<Dayjs>;
  datePicked: Dayjs | null;
}) => {
  const { currentMonth, onMonthChange } = props;

  const selectNextMonth = () => {
    onMonthChange(currentMonth.add(1, "month"), "left");
  };
  const selectPreviousMonth = () => {
    onMonthChange(currentMonth.subtract(1, "month"), "right");
  };

  const styles = getStyles(false);
  return (
    <CustomCalendarHeaderRoot>
      <Stack
        width={"100%"}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack spacing={1} direction="row">
          <IconButton onClick={selectPreviousMonth} title="Previous month">
            <ChevronLeft />
          </IconButton>
        </Stack>
        <Typography variant="body2" fontWeight={"700"} fontSize={"16px"}>
          {currentMonth.format("MMMM YYYY")}
        </Typography>
        <Stack spacing={1} direction="row">
          <IconButton onClick={selectNextMonth} title="Next month">
            <ChevronRight />
          </IconButton>
        </Stack>
      </Stack>
    </CustomCalendarHeaderRoot>
  );
};

const CalenderLabel = ({
  fontWeight = "400",
  title,
  color,
}: {
  title: string;
  fontWeight?: "700" | "600" | "500" | "400";
  color?: string;
}) => {
  return (
    <Typography
      fontFamily={"Inter"}
      fontSize={14}
      fontWeight={fontWeight}
      color={color ?? "#455042"}
      noWrap
      textAlign={"center"}
    >
      {title}
    </Typography>
  );
};
const getStyles = (isBasic: boolean) => ({
  container: {
    display: "flex",
  },

  dateComponent: {
    borderBottom: "1px #B7C1B6 solid",
    width: 328,
  },
  labelContainer: {
    height: isBasic ? 35 : "55px",
    minWidth: isBasic ? "undefined" : 250,
    backgroundColor: "white",
    borderRadius: "8px",
    border: "1px #D0D5DD solid",
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    justifyContent: "left",
    cursor: "pointer",
    padding: "0 16px",
  },
  pickedDayIndicator: {
    width: 5,
    height: 5,
    position: "absolute",
    marginTop: 3.2,
    marginLeft: 2.2,
    zIndex: 10,
  },
  dayPickerContainer: {
    display: "flex",
    flexDirection: "column",
  },
  dayContainer: {
    "&.MuiPickersDay-root.Mui-selected": {
      backgroundColor: `#04BB47 !important`,
      color: "white",
      fontSize: "14px",
    },
  },
  model: {
    position: "absolute",
    marginTop: isBasic ? "10px" : "90px",
    zIndex: 1000,
    transition: "all 0.3s ease-in-out",
    boxShadow: `0px 8px 8px -4px rgba(230, 227, 227,0.8)`,
    backgroundColor: "white",
    border: "1px #B7C1B6 solid",
    borderRadius: "8px",
  },
});
